import apiClient, { del, get, post, put } from '@/lib/api/client';
import { buildUrl } from '@/lib/utils/url';
import type { BackendPagedResponse, PagedResult } from '@/types/api';
import { toPagedResult } from '@/types/api';
import { ENDPOINTS } from '@/constants/endpoints';

import type {
  Chatbot,
  ChatbotCreatePayload,
  ChatbotSearchParams,
  ChatbotUpdatePayload,
  ChatbotUser,
  ChatbotUserSearchParams,
  MyChatbot,
} from '../types/chatbot';

export const chatbotApi = {
  /** GET /api/Chatbot */
  getChatbots: async (
    params?: ChatbotSearchParams,
  ): Promise<PagedResult<Chatbot>> => {
    const url = buildUrl(ENDPOINTS.CHATBOT.LIST, {
      Page: params?.Page,
      PageSize: params?.PageSize,
      Sort: params?.Sort,
      Direction: params?.Direction,
      Code: params?.Code,
      ChatbotName: params?.ChatbotName,
      Status: params?.Status,
      Query: params?.Query,
    });

    const response = await apiClient.get<BackendPagedResponse<Chatbot>>(url);
    return toPagedResult(response.data);
  },

  /** GET /api/Chatbot/{id} */
  getChatbot: async (id: string): Promise<Chatbot> => {
    return get<Chatbot>(ENDPOINTS.CHATBOT.BY_ID(id));
  },

  /** POST /api/Chatbot */
  createChatbot: async (data: ChatbotCreatePayload): Promise<string> => {
    return post<string>(ENDPOINTS.CHATBOT.LIST, data);
  },

  /** PUT /api/Chatbot */
  updateChatbot: async (data: ChatbotUpdatePayload): Promise<string> => {
    return put<string>(ENDPOINTS.CHATBOT.LIST, data);
  },

  /** DELETE /api/Chatbot/{id} */
  deleteChatbot: async (id: string): Promise<string> => {
    return del<string>(ENDPOINTS.CHATBOT.BY_ID(id));
  },

  /** GET /api/Chatbot/users */
  getChatbotUsers: async (
    params: ChatbotUserSearchParams,
  ): Promise<PagedResult<ChatbotUser>> => {
    const url = buildUrl(ENDPOINTS.CHATBOT.USERS, {
      ChatbotId: params.ChatbotId,
      Page: params.Page,
      PageSize: params.PageSize,
      Filter: params.Filter,
    });

    const response = await apiClient.get<BackendPagedResponse<ChatbotUser>>(url);
    return toPagedResult(response.data);
  },

  /**
   * POST /api/Chatbot/{id}/users
   * Replaces the whole user set — send every user that should keep access.
   */
  assignUsers: async (id: string, userIds: string[]): Promise<boolean> => {
    return post<boolean>(ENDPOINTS.CHATBOT.ASSIGN_USERS(id), { userIds });
  },

  /** DELETE /api/Chatbot/{id}/users/{userId} */
  removeUser: async (id: string, userId: string): Promise<boolean> => {
    return del<boolean>(ENDPOINTS.CHATBOT.REMOVE_USER(id, userId));
  },

  /** GET /api/Chatbot/my */
  getMyChatbots: async (): Promise<MyChatbot[]> => {
    return get<MyChatbot[]>(ENDPOINTS.CHATBOT.MY);
  },

  /** PUT /api/Chatbot/sort — orderedIds theo thứ tự hiển thị mong muốn. */
  sortChatbots: async (orderedIds: string[]): Promise<boolean> => {
    return put<boolean>(
      ENDPOINTS.CHATBOT.SORT,
      orderedIds.map((id, index) => ({ id, displayOrder: index + 1 })),
    );
  },
};

export default chatbotApi;
